import { ChangeDetectorRef, Component, Inject, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePickerPresets, GlobalFunctions } from '@global/GlobalFunctions';
import { Account } from '@services/accounts.service';
import { ActionsDataService } from '@services/actions-data.service';
import { LevelAsset, AssetsDataService } from '@services/assets-data.service';
import { WebAppUser, AuthenticationService } from '@services/authentication.service';
import { Subject, combineLatest, takeUntil, distinctUntilChanged, filter, finalize } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { AssetModelsComponent } from '../asset-models/asset-models.component';
import { DataPoint } from '@services/datapoints-data.service';
import { convertEpochToDateString } from '@functions/functions';
import { Carousel } from '@syncfusion/ej2-angular-navigations';
import { AssetDistribution, ModelsService, TestModelData } from '@services/models.service';

export class SparklineData {
	DatapointID: number;
	DPTitle: string;
	GraphTitle: string;
	GraphType: number; //0 = AverageZScore, 1 = PercentageAbove, 2 = AverageZScoreAbove
	Values: object[];
}

export class TestResult {
	Image?: string;
	DatapointZScore: {
		Datapoint: DataPoint,
		ZScore: number
	};
	percentage: {
		DatapointID: number,
		percentageAbove: number,
		percentageBelow: number,
		createEvent: boolean,
		eventText: string,
		ZScore: number
	};
}

export class TestDistributionData {
	Asset?: LevelAsset;
	Distribution?: AssetDistribution;
}

@Component({
	selector: 'app-test-distribution',
	templateUrl: './test-distribution.component.html',
	styleUrls: ['./test-distribution.component.scss']
})
export class TestDistributionComponent implements OnDestroy {

	/** Selected Account ID */
	public selectedAccountUID: string = ""

	/** Selected Organisation ID */
	public selectedOrganisationUID: string = ""

	/** Selected Asset */
	public selectedAsset: LevelAsset;

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** account */
	public account: Account;

	/** currentUser */
	public currentUser: WebAppUser;

	/** shortMonths */
	private shortMonths: string[] = [
		"Jan", "Feb", "Mar", "Apr", "May", "Jun",
		"Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
	];

	/** distribution */
	public distribution: AssetDistribution;

	/** testImage */
	public testImage: string = "";

	/** numDays */
	public numDays: number = 7;

	/** spinner */
	public spinner: boolean = false;

	/** DatapointZScores */
	public DatapointZScores: { Datapoint: DataPoint, ZScore: number }[] = [];

	/** DateRangeSelected */
	public DateRangeSelected: boolean = true;

	/** start */
	public start: number;

	/** end */
	public end: number;

	/** validRangeSelected */
	public validRangeSelected: boolean = false;

	/** today */
	public today: Date = new Date();

	/** percentages */
	public percentages: {
		DatapointID: number,
		percentageAbove: number,
		percentageBelow: number,
		createEvent: boolean,
		eventText: string,
		ZScore: number
	}[] = [];

	/** allInfo */
	public allInfo: TestResult[] = [];

	/** AutoResult */
	public AutoResult: TestResult[][] = [];

	/** AdvancedTest */
	public AdvancedTest: boolean = false;

	/** numWeeks */
	public numWeeks: number = 5;

	/** AdvancedStart */
	public AdvancedStart: number = 0;

	/** treatAsAutomatic */
	public treatAsAutomatic: boolean = false;

	/** sparkLineData */
	public sparkLineData: SparklineData[] = [];

	/** sparklineDoubleData */
	public sparklineDoubleData: [SparklineData, SparklineData, SparklineData][] = [];

	/** SparkLineTooltipSettings */
	public SparkLineTooltipSettings: object = {
		visible: true,
		format: '${xval} : ${yval}',
		trackLineSettings: {
			visible: true,
			color: 'red',
			width: 2
		}
	};

	/** sparklines */
	// @ViewChildren('sparkline') sparklines: QueryList<SparklineComponent>;

	/** usingAverage */
	public usingAverage: boolean = false;

	public axisSettings: object = {
		lineSettings: {
			color: 'darkgray',
			width: 1,
			visible: true
		},
		value: 0
	};

	/** earliestDate */
	public earliestDate: Date;

	/** maxWeeks */
	public maxWeeks: number = 52;

	/** selectedCarouselIndex */
	public selectedCarouselIndex: number = 0;

	/** autoplay */
	public autoplay: boolean = false;

	/** My Chart */
	@ViewChild('carousel') public carousel: Carousel;

	/** recreationFrequency */
	public recreationFrequency: number = 7;

	/** overlapPeriod */
	public overlapPeriod: number = 0;

	/** datePickerPresets - #71 */
	public datePickerPresets = DatePickerPresets;

	/**
	 * Default constructor
	 * @param route route 
	 * @param router router 
	 * @param assetsDataService assetsDataService  
	 * @param siteSubscriptions siteSubscriptions 
	 * @param actionsDataService actionsDataService 
	 * @param dialog dialog 
	 * @param authenticationService authenticationService  
	 * @param globalFunctions globalFunctions 
	 * @param location location 
	 */
	constructor(
		private route: ActivatedRoute,
		private router: Router,
		public assetsDataService: AssetsDataService,
		public modelsService: ModelsService,
		public siteSubscriptions: siteSubscriptions,
		public actionsDataService: ActionsDataService,
		public dialog: MatDialog,
		private authenticationService: AuthenticationService,
		private globalFunctions: GlobalFunctions,
		public dialogRef: MatDialogRef<AssetModelsComponent>,
		@Inject(MAT_DIALOG_DATA) public dialogData: TestDistributionData,
		private cdr: ChangeDetectorRef
	) {
		combineLatest([
			this.siteSubscriptions.selectedAccountGUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.queryParams.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.paramMap.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([selectedAccountGUID, selectedAccount, selectedOrganisationUID, currentUser, queryParams, paramMap]) =>
				!!selectedAccountGUID && !!selectedAccount && !!selectedOrganisationUID && !!currentUser && !!queryParams)
		).subscribe({
			next: ([selectedAccountGUID, selectedAccount, selectedOrganisationUID, currentUser, queryParams, paramMap]) => {
				this.selectedAccountUID = selectedAccountGUID;
				this.selectedOrganisationUID = selectedOrganisationUID;
				this.currentUser = currentUser;
				this.account = selectedAccount;
				this.account = this.siteSubscriptions.normalizeFeatures(this.account) as Account;
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/** ngOnInit */
	ngOnInit(): void {
		if (this.dialogData) {
			if (this.dialogData.Asset) {
				this.selectedAsset = this.dialogData.Asset;
			}
			if (this.dialogData.Distribution) {
				this.distribution = this.dialogData.Distribution;
				if (this.distribution.Sensitivity == 0) {
					this.distribution.Sensitivity = 50;
				}
				this.usingAverage = this.distribution.Sensitivity == 50;
				this.treatAsAutomatic = this.distribution.IsAutomatic;
				// this.usingAverage = true; // Force using average for testing
			}
		}
		this.earliestDate = new Date(this.today.getTime() - (this.numWeeks * 7 * 24 * 60 * 60 * 1000));
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		this.dialogRef.close();
	}

	/**
	 * testDistribution
	 */
	testDistribution() {
		if (this.DateRangeSelected && !this.validRangeSelected) {
			this.globalFunctions.DisplaySnackBar({
				message: "Please select a valid date range or number of days.",
				duration: 2000,
				action: ""
			});
			return;
		}

		this.displaySpinner(true);

		this.testImage = "";

		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		let timeData: TestModelData = new TestModelData(
			this.DateRangeSelected,
			this.DateRangeSelected ? 0 : this.numDays,
			this.DateRangeSelected ? this.start : 0,
			this.DateRangeSelected ? this.end : 0
		);

		this.modelsService.testAssetDistribution$(this.selectedAccountUID, this.selectedOrganisationUID, this.distribution, timeData).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((image) => !!image),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.displaySpinner(false);
			})
		).subscribe({
			next: (output: any) => {
				let JSONOutput = JSON.parse(output);
				this.testImage = JSONOutput.image;
				this.DatapointZScores = JSONOutput.dpZScores.map((x: { Item1: DataPoint, Item2: number }) => ({ Datapoint: x.Item1, ZScore: x.Item2 })) || [];
				this.percentages = JSONOutput.percentages.map((x: {
					Item1: number,
					Item2: number,
					Item3: number,
					Item4: boolean,
					Item5: string,
					Item6: number
				}) => ({
					DatapointID: x.Item1,
					percentageAbove: x.Item2,
					percentageBelow: x.Item3,
					createEvent: x.Item4,
					eventText: x.Item5,
					ZScore: x.Item6
				})) || [];
				for (let i = 0; i < this.DatapointZScores.length; i++) {
					this.allInfo[i] = { DatapointZScore: this.DatapointZScores[i], percentage: this.percentages[i] };
				}
			},
			error: (error) => {
				if (error.status != 404) {
					this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.testAssetDistribution$", false);
				}
			},
			//complete: () => { console.log("this.AssetsDataService.getAssetDataPoints$.subscribe: Complete"); }
		});
	}

	/**
	 * Show the Spinner
	 * @param show
	 */
	displaySpinner(show: boolean) {
		// this.globalFunctions.displaySpinner(show);
		this.spinner = show;
	}

	/**
	 * changeOption
	 * @param isDateRange isDateRange 
	 */
	changeOption(isDateRange: boolean) {
		this.DateRangeSelected = isDateRange;
		if (!isDateRange) {
			this.start = 0;
			this.end = 0;
			this.validRangeSelected = false;
		}
	}

	/** dateRangeSelected */
	dateRangeSelected(event: any) {
		this.start = event.startDate.getTime() || 0;
		this.end = event.endDate.getTime() || 0;
		if ((this.start > 0 && this.end > 0) && (this.start < this.end) && (this.start < Date.now() && this.end < Date.now())) {
			this.validRangeSelected = true;
		}
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/** getShortDate */
	getShortDate(date: Date): string {
		let result: string = "";
		result += date.getDate();
		result +=
			" " +
			this.shortMonths[date.getMonth()] +
			" " +
			date.getHours().toString().padStart(2, "0") +
			":" +
			date.getMinutes().toString().padStart(2, "0");

		return result;
	}

	/**
	 * getSelectedRange
	 * @returns 
	 */
	getSelectedRange(): string {
		if (this.DateRangeSelected && this.validRangeSelected) {
			return `${this.getDateWithoutTime(this.start)} - ${this.getDateWithoutTime(this.end)}`;
		} else {
			return "";
		}
	}

	/**
	 * getDateWithoutTime
	 * @param epoch epoch 
	 * @returns 
	 */
	getDateWithoutTime(epoch: number): string {
		const date = new Date(epoch);
		return `${date.getDate().toString().padStart(2, '0')} ${this.shortMonths[date.getMonth()]} ${date.getFullYear()}`;
	}

	/**
	 * findPercentageForDPZ
	 * @param dpz dpz 
	 * @returns 
	 */
	findPercentageForDPZ(dpz: { Datapoint: DataPoint, ZScore: number }): {
		percentageAbove: number,
		percentageBelow: number,
		createEvent: boolean,
		eventText: string,
		ZScore: number
	} | undefined {
		return this.percentages.find(p => p.DatapointID === dpz.Datapoint.DataPointID);
	}

	/**
	 * testDistributionAuto
	 */
	testDistributionAuto() {
		if (this.AdvancedStart == 0) {
			this.globalFunctions.DisplaySnackBar({
				message: "Please select a valid start date.",
				duration: 2000,
				action: ""
			});
			return;
		}

		this.displaySpinner(true);
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.AutoResult = [];
		this.sparkLineData = [];
		this.sparklineDoubleData = [];
		this.selectedCarouselIndex = 0;

		this.modelsService.testAssetDistributionAuto$(this.selectedAccountUID, this.selectedOrganisationUID, this.distribution, this.AdvancedStart,
			this.treatAsAutomatic, this.numWeeks, this.recreationFrequency, this.overlapPeriod).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((output) => !!output),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
					this.displaySpinner(false);
				})
			).subscribe({
				next: (output: any) => {
					this.AutoResult = output.map((x: string) => {
						let JSONOutput = JSON.parse(x);
						let DatapointZScores = JSONOutput.dpZScores.map((x: { Item1: DataPoint, Item2: number }) => ({ Datapoint: x.Item1, ZScore: x.Item2 })) || [];
						let percentages = JSONOutput.percentages.map((x: {
							Item1: number,
							Item2: number,
							Item3: number,
							Item4: boolean,
							Item5: string,
							Item6: number
						}) => ({
							DatapointID: x.Item1,
							percentageAbove: x.Item2,
							percentageBelow: x.Item3,
							createEvent: x.Item4,
							eventText: x.Item5,
							ZScore: x.Item6
						})) || [];
						let allInfo: TestResult[] = [];
						for (let i = 0; i < DatapointZScores.length; i++) {
							allInfo[i] = { Image: JSONOutput.image, DatapointZScore: DatapointZScores[i], percentage: percentages[i] };
						}
						return allInfo;
					})
					this.AutoResult = this.AutoResult.filter((x) => x.length > 0);
					this.calulateSparkLineData();
				},
				error: (error) => {
					if (error.status != 404) {
						this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.testAssetDistribution$", false);
					}
				},
				//complete: () => { console.log("this.AssetsDataService.getAssetDataPoints$.subscribe: Complete"); }
			});
	}

	/**
	 * toggleAdvancedTest
	 * @param event event 
	 */
	toggleAdvancedTest(event: any) {
		this.AdvancedTest = event.checked;
		this.start = 0;
		this.end = 0;
		this.validRangeSelected = false;
		this.AdvancedStart = 0;
		this.numDays = 7;
		this.numWeeks = 5;
		this.treatAsAutomatic = this.distribution.IsAutomatic;
		this.AutoResult = [];
		this.testImage = "";
		this.sparkLineData = [];
		this.allInfo = [];
		this.DatapointZScores = [];
		this.sparklineDoubleData = [];
		this.selectedCarouselIndex = 0;
		this.recreationFrequency = 7;
		this.overlapPeriod = 0;
	}

	/**
	 * toggleTreatAsAuto
	 * @param event event 
	 */
	toggleTreatAsAuto(event: any) {
		this.treatAsAutomatic = event.checked;
	}

	/**
	 * selectStartDate
	 * @param event event 
	 */
	selectStartDate(event: any) {
		this.AdvancedStart = event.value.getTime() || 0;
		this.maxWeeks = Math.floor((this.today.getTime() - this.AdvancedStart) / (7 * 24 * 60 * 60 * 1000));
	}

	/**
	 * calulateSparkLineData
	 */
	calulateSparkLineData() {
		if (this.usingAverage) {
			for (let i = 0; i < this.AutoResult.length; i++) {
				this.AutoResult[i].forEach((y: TestResult) => {
					let item = this.sparkLineData.find((x) => x.DatapointID == y.DatapointZScore.Datapoint.DataPointID);
					if (!item) {
						let dp = y.DatapointZScore.Datapoint;
						item = {
							DatapointID: dp.DataPointID,
							DPTitle: dp.DPTitle,
							GraphTitle: "Average Z-Score",
							GraphType: 0, // Average Z-Score
							Values: []
						};
						this.sparkLineData.push(item);
					}
					item.Values.push({
						x: i,
						xval: `Week ${i + 1}`,
						yval: y.DatapointZScore.ZScore
					});
				})
			}
		} else {
			for (let i = 0; i < this.AutoResult.length; i++) {
				this.AutoResult[i].forEach((y: TestResult) => {
					let item = this.sparkLineData.find((x) => x.DatapointID == y.percentage.DatapointID && x.GraphType == 1);
					if (!item) {
						let dp = y.DatapointZScore.Datapoint;
						item = {
							DatapointID: dp.DataPointID,
							DPTitle: dp.DPTitle,
							GraphTitle: "Percentage of Data Above Tolerance",
							GraphType: 1, // Percentage Above
							Values: []
						};
						this.sparkLineData.push(item);
					}
					item.Values.push({
						x: i,
						xval: `Week ${i + 1}`,
						yval: y.percentage.percentageAbove
					});
					let item2 = this.sparkLineData.find((x) => x.DatapointID == y.percentage.DatapointID && x.GraphType == 2);
					if (!item2) {
						let dp = y.DatapointZScore.Datapoint;
						item2 = {
							DatapointID: dp.DataPointID,
							DPTitle: dp.DPTitle,
							GraphTitle: "Average Z-Score Above Tolerance",
							GraphType: 2, // Average Z-Score Above
							Values: []
						};
						this.sparkLineData.push(item2);
					}
					item2.Values.push({
						x: i,
						xval: `Week ${i + 1}`,
						yval: y.percentage.ZScore
					});
					let item3 = this.sparkLineData.find((x) => x.DatapointID == y.percentage.DatapointID && x.GraphType == 0);
					if (!item3) {
						let dp = y.DatapointZScore.Datapoint;
						item3 = {
							DatapointID: dp.DataPointID,
							DPTitle: dp.DPTitle,
							GraphTitle: "Average Z-Score",
							GraphType: 0, // Average Z-Score
							Values: []
						};
						this.sparkLineData.push(item3);
					}
					item3.Values.push({
						x: i,
						xval: `Week ${i + 1}`,
						yval: y.DatapointZScore.ZScore
					});
				})
			}
			for (let i = 0; i < this.sparkLineData.length; i++) {
				if (i % 3 == 0) {
					this.sparklineDoubleData.push([this.sparkLineData[i], this.sparkLineData[i + 1], this.sparkLineData[i + 2]]);
				}
			}
		}
		this.cdr.detectChanges();
		// setTimeout(() => {
		// 	this.sparklines.forEach((sparkline) => sparkline.refresh());
		// }, 0);
	}

	/**
	 * onNumDaysChange
	 * @param event event 
	 */
	onNumDaysChange(event: any) {
		this.earliestDate = new Date(this.today.getTime() - (event.value * 7 * 24 * 60 * 60 * 1000));
	}

	/**
	 * goToWeek
	 * @param event event  
	 */
	goToWeek(event: any) {
		this.selectedCarouselIndex = event.value - 1;
	}

	/**
	 * toggleAutoplay
	 * @param event event
	 */
	toggleAutoplay(event: any) {
		this.autoplay = event.checked;
		if (this.carousel) {
			this.carousel.autoPlay = this.autoplay;
		}
	}
}
